import { supabase, SupabaseNotConfiguredError } from "../supabase";
import type { CardState } from "../db";
import type { Database } from "../database.types";

type CardStateDbRow = Database["public"]["Tables"]["card_states"]["Row"];
type CardStateInsert = Database["public"]["Tables"]["card_states"]["Insert"];

function rowToCardState(row: CardStateDbRow & { sync_version?: number }): CardState {
  return {
    syncVersion: row.sync_version,
    key: row.key,
    conceptId: row.concept_id ?? "",
    direction: (row.direction as CardState["direction"]) ?? ("en>ru" as CardState["direction"]),
    deckId: row.deck_id ?? undefined,
    nextReview: row.next_review ?? 0,
    interval: row.interval ?? 1,
    repetition: row.repetition ?? 0,
    efactor: row.efactor ?? 2.5,
  };
}

function cardStateToInsert(userId: string, cs: CardState): CardStateInsert {
  return {
    user_id: userId,
    key: cs.key,
    concept_id: cs.conceptId,
    direction: cs.direction,
    deck_id: cs.deckId ?? null,
    next_review: cs.nextReview,
    interval: cs.interval,
    repetition: cs.repetition,
    efactor: cs.efactor,
    updated_at: new Date().toISOString(),
  };
}

const CHUNK_SIZE = 500;

export async function pushCardStates(userId: string, states: CardState[]): Promise<void> {
  if (!supabase) throw new SupabaseNotConfiguredError();
  for (let i = 0; i < states.length; i += CHUNK_SIZE) {
    const chunk = states.slice(i, i + CHUNK_SIZE).map((cs) => cardStateToInsert(userId, cs));
    const { error } = await supabase.from("card_states").upsert(chunk, { onConflict: "user_id,key" });
    if (error) throw error;
  }
}

export async function pushCardState(userId: string, cs: CardState): Promise<void> {
  if (!supabase) throw new SupabaseNotConfiguredError();
  const { error } = await supabase
    .from("card_states")
    .upsert(cardStateToInsert(userId, cs), { onConflict: "user_id,key" });
  if (error) throw error;
}

export async function pullCardStates(userId: string): Promise<CardState[]> {
  if (!supabase) throw new SupabaseNotConfiguredError();
  const rows: CardStateDbRow[] = [];
  let after: string | undefined;
  for (;;) {
    let query = supabase.from("card_states").select("*")
      .eq("user_id", userId).order("key").limit(500);
    if (after !== undefined) query = query.gt("key", after);
    const { data, error } = await query;
    if (error) throw error;
    rows.push(...(data ?? []));
    if ((data ?? []).length < 500) break;
    after = data![data!.length - 1].key;
  }
  return rows.map(rowToCardState);
}

export async function deleteCardStateRemote(userId: string, key: string): Promise<void> {
  if (!supabase) throw new SupabaseNotConfiguredError();
  const { error } = await supabase
    .from("card_states")
    .delete()
    .eq("user_id", userId)
    .eq("key", key);
  if (error) throw error;
}
